import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  ArrowRight, 
  Slack, 
  FileCode, 
  GitBranch, 
  Check,
  FolderKanban,
  FileText
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ProjectCreatePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availableSources, setAvailableSources] = useState([]);

  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    type: 'product',
    color: '#3b82f6',
    icon: 'folder'
  });

  const [selectedSources, setSelectedSources] = useState([]);
  const [sourceConfigs, setSourceConfigs] = useState({});
  const [documents, setDocuments] = useState('');

  useEffect(() => {
    loadAvailableSources();
  }, []);

  const loadAvailableSources = async () => {
    try {
      const response = await api.get('/sources');
      setAvailableSources(response.data);
    } catch (error) {
      console.error('Failed to load sources:', error);
    }
  };

  const handleProjectInfoSubmit = (e) => {
    e.preventDefault();
    if (!projectData.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setStep(2);
  };

  const handleSourceToggle = (sourceId) => {
    if (selectedSources.includes(sourceId)) {
      setSelectedSources(selectedSources.filter(id => id !== sourceId));
      const newConfigs = { ...sourceConfigs };
      delete newConfigs[sourceId];
      setSourceConfigs(newConfigs);
    } else {
      setSelectedSources([...selectedSources, sourceId]);
    }
  };

  const handleSourceConfigChange = (sourceId, field, value) => {
    setSourceConfigs({
      ...sourceConfigs,
      [sourceId]: {
        ...sourceConfigs[sourceId],
        [field]: value
      }
    });
  };

  const handleCreateProject = async () => {
    setLoading(true);
    try {
      // Create project
      const projectResponse = await api.post('/projects', projectData);
      const projectId = projectResponse.data.id;

      // Add sources to project
      for (const sourceId of selectedSources) {
        const config = sourceConfigs[sourceId] || {};
        if (config.filter_label && config.filter_value) {
          await api.post(`/projects/${projectId}/sources`, {
            source_conn_id: sourceId,
            filter_type: 'channel',
            filter_value: config.filter_value,
            filter_label: config.filter_label
          });
        }
      }

      toast.success('Project created successfully!');
      navigate(`/projects/${projectId}`);
    } catch (error) {
      toast.error('Failed to create project');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'slack':
        return Slack;
      case 'jira':
        return FileCode;
      case 'github':
        return GitBranch;
      default:
        return FileCode;
    }
  };

  const progressPercent = (step / 3) * 100;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="project-create-page">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => step === 1 ? navigate('/projects') : setStep(step - 1)}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <h1 className="text-3xl font-bold mb-2">Create New Project</h1>
          <p className="text-muted-foreground">Set up a project to organize your work</p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Step {step} of 3</span>
            <span className="font-medium">{progressPercent.toFixed(0)}% complete</span>
          </div>
          <Progress value={progressPercent} />
        </div>

        {/* Step 1: Project Info */}
        {step === 1 && (
          <form onSubmit={handleProjectInfoSubmit} className="space-y-6">
            <Card className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FolderKanban className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-name">Project Name *</Label>
                    <Input
                      id="project-name"
                      placeholder="Website Redesign"
                      value={projectData.name}
                      onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                      required
                      data-testid="project-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="project-description">Description</Label>
                    <Textarea
                      id="project-description"
                      placeholder="Describe what this project is about, its goals, and key deliverables..."
                      value={projectData.description}
                      onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                      rows={4}
                      data-testid="project-description-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      This helps AI understand your project context for better insights
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="project-type">Type</Label>
                      <select
                        id="project-type"
                        className="w-full p-2 border border-border rounded-md bg-background"
                        value={projectData.type}
                        onChange={(e) => setProjectData({ ...projectData, type: e.target.value })}
                      >
                        <option value="product">Product</option>
                        <option value="marketing">Marketing</option>
                        <option value="sales">Sales</option>
                        <option value="hr">HR</option>
                        <option value="general">General</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="project-color">Color</Label>
                      <div className="flex gap-2">
                        {['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'].map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 ${
                              projectData.color === color ? 'border-foreground' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setProjectData({ ...projectData, color })}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex justify-end">
              <Button type="submit" data-testid="next-step-btn">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </form>
        )}

        {/* Step 2: Connect Sources */}
        {step === 2 && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-semibold mb-2">Connect your tools</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select the channels, repos, or boards that belong to this project
              </p>

              {availableSources.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-lg">
                  <p className="text-muted-foreground mb-4">No integrations connected yet</p>
                  <Button variant="outline" onClick={() => navigate('/settings/integrations')}>
                    Connect Integrations
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableSources.map((source) => {
                    const Icon = getSourceIcon(source.source_type);
                    const isSelected = selectedSources.includes(source.id);

                    return (
                      <div key={source.id} className="space-y-3">
                        <div
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => handleSourceToggle(source.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{source.source_type}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {source.metadata.workspace_name || source.metadata.project_name || 'Connected'}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {source.source_type === 'slack' && 'Send notifications to channels'}
                                {source.source_type === 'jira' && 'Sync issues and workflows'}
                                {source.source_type === 'github' && 'Link pull requests and commits'}
                              </p>
                            </div>
                            {isSelected && (
                              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <Check className="h-4 w-4 text-primary-foreground" />
                              </div>
                            )}
                          </div>
                        </div>

                        {isSelected && (
                          <div className="ml-14 space-y-3 pb-2">
                            <div className="space-y-2">
                              <Label>Channel/Project Name</Label>
                              <Input
                                placeholder="#general, website-proj, main-repo"
                                value={sourceConfigs[source.id]?.filter_label || ''}
                                onChange={(e) => handleSourceConfigChange(source.id, 'filter_label', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>ID or Key</Label>
                              <Input
                                placeholder="C1234567890, PROJ-KEY, org/repo"
                                value={sourceConfigs[source.id]?.filter_value || ''}
                                onChange={(e) => handleSourceConfigChange(source.id, 'filter_value', e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => setStep(3)} data-testid="next-step-btn">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Add Documents (Optional) */}
        {step === 3 && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="font-semibold mb-1">Add relevant documents (Optional)</h3>
                    <p className="text-sm text-muted-foreground">
                      Add links or paste content that provides context about this project. This helps AI generate better insights.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="documents">Documents, links, or context</Label>
                    <Textarea
                      id="documents"
                      placeholder="Paste links to Google Docs, Notion pages, Figma files, or any relevant context...&#10;&#10;Example:&#10;- PRD: https://docs.google.com/...&#10;- Design: https://figma.com/...&#10;- Previous quarter goals: ..."
                      value={documents}
                      onChange={(e) => setDocuments(e.target.value)}
                      rows={8}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Summary */}
            <Card className="p-6 bg-muted/50">
              <h3 className="font-semibold mb-4">Project Summary</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-muted-foreground">Name:</span>
                  <p className="font-medium">{projectData.name}</p>
                </div>
                {projectData.description && (
                  <div>
                    <span className="text-sm text-muted-foreground">Description:</span>
                    <p className="text-sm">{projectData.description}</p>
                  </div>
                )}
                <div>
                  <span className="text-sm text-muted-foreground">Sources:</span>
                  <p className="text-sm">
                    {selectedSources.length === 0 
                      ? 'None (can be added later)' 
                      : `${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''} connected`}
                  </p>
                </div>
              </div>
            </Card>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button 
                onClick={handleCreateProject} 
                disabled={loading}
                data-testid="create-project-btn"
              >
                {loading ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
